import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useForm, type FieldValues } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import QRCode from 'react-qr-code';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { VerifyIcon } from '../../../assets/Icon';
import { emailPattern } from '../../../constants/patterns';
import AuthContext from '../../../contexts/AuthContext';
import WalletContext from '../../../contexts/WalletContext';
import useAPI from '../../../hooks/useAPI';
import Loader from '../../common/Loader';
import Modal from '../../common/modal/Modal';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import TransactionSuccessModal from '../transaction-success-dialog';
import styles from './style.module.scss';
import TokenListContext from '../../../contexts/TokenListContext';

type ReceiveCashModalProps = {
  open: boolean;
  onClose: () => void;
};

interface CreatedBy {
  id: string;
  role: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

interface TransactionData {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: CreatedBy;
  numberOfToken: string;
  remarks: string;
  status: string;
  tokenAmount: string;
  tokenType: string;
  transactionDate: string;
  transactionId: string;
  type: string;
  wallet: string;
}

const schema = z.object({
  amount: z
    .string()
    .min(1, 'Total tokens must be greater than 0')
    .refine(val => Number(val) !== 0, {
      message: 'Enter a valid number of tokens',
    }),
  mobile: z.string().refine(value => {
    const mobileRegex = /^[0-9]{10}$/; // Will be used in future
    return emailPattern.test(value);
  }, 'Please enter a valid email'),
  description: z
    .string()
    .max(200, 'Description should not exceed 200 characters')
    .optional(),
});

const ReceiveCashModal = ({ onClose, open }: ReceiveCashModalProps) => {
  const [cashReceive, setCashReceive] = useState(false);
  const [receiverData, setReciverData] = useState<any>(null);
  const [cashReceiveInfo, setCashReceiveInfo] =
    useState<TransactionData | null>(null);
  const { userData } = useContext(AuthContext);
  const { tokenList } = useContext(TokenListContext);
  const { refetchTransactions, refetch: walletBalanceRefetch } =
    useContext(WalletContext);
  const [verifyError, setVerifyError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const { refetch, data, isLoading } = useAPI({
    url: 'token/user/receive/cash',
    method: 'POST',
    onSuccess: data => {
      setCashReceive(true);
      setReciverData(null);
      reset();
      refetchTransactions();
      walletBalanceRefetch();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
      reset();
      onClose();
    },
  });

  const { refetch: findUser, isLoading: isLoadingMembers } = useAPI({
    url: '/auth/email/find',
    method: 'POST',
    enabled: !!watch('mobile'),
    onSuccess: data => {
      setReciverData(data?.result);
    },
    onError: error => {
      setVerifyError(error?.response?.data?.message || '');
    },
  });

  const onSubmit = (data: FieldValues) => {
    if (tokenList[0]?.tokenId) {
      const payload = {
        amount: data?.amount,
        receiverWallet: receiverData?.wallet[0]?.walletAddress,
        description: data?.description,
      };
      refetch(payload);
    } else {
      toast.error(
        `Your token request cannot be processed as no tokens have been created yet. Please contact your administrator to generate the necessary tokens..`,
      );
    }
  };

  useEffect(() => {
    if (!errors.mobile) {
      const debounceTimer = setTimeout(() => {
        setReciverData(null);
        setVerifyError('');
        findUser({ email: watch('mobile') });
      }, 500);

      return () => clearTimeout(debounceTimer);
    }
  }, [watch('mobile'), errors.mobile]);

  useEffect(() => {
    if (!open) {
      reset();
      setCashReceive(false);
      setReciverData(null);
    }
  }, [open]);

  return (
    <>
      <TransactionSuccessModal
        open={cashReceive}
        title="Your token request has been successfully sent. You will be notified once it is processed."
        isCloseIconVisible={true}
        onClose={() => {
          setCashReceive(false);
          onClose();
        }}
      />
      {!cashReceive && (
        <Modal
          open={open}
          title="Receive Token"
          onClose={onClose}
          isCloseIconVisible={!isLoading}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.qrInfo}>
              <QRCode
                size={70}
                // style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                value={userData?.wallet[0]?.walletAddress || ''}
                viewBox={`0 0 256 256`}
              />
              {/* <img src={QRCode} alt="QR Code" /> */}
              <div className={styles.walletInfo}>
                <label>Wallet Address</label>
                <span>
                  {userData?.wallet[0]?.walletAddress || 'No walletdsd address'}
                </span>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="" className={styles.label}>
                Enter Amount <span className="text-danger">*</span>
              </Form.Label>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <div style={{ position: 'relative' }}>
                    <NumericFormat
                      thousandSeparator
                      decimalScale={0}
                      allowNegative={false}
                      customInput={Input}
                      onValueChange={value => field.onChange(value.value)}
                      error={errors.amount?.message as string}
                      style={{
                        paddingRight: tokenList?.[0]?.tokenSymbol
                          ? '60px'
                          : '10px',
                      }}
                    />
                    {tokenList?.[0]?.tokenSymbol && (
                      <span
                        style={{
                          position: 'absolute',
                          right: '15px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#666',
                          fontWeight: 600,
                          pointerEvents: 'none',
                        }}
                      >
                        {tokenList[0].tokenSymbol}
                      </span>
                    )}
                  </div>
                )}
              />
            </Form.Group>

            <Input
              label="Enter Email"
              className="mb-3"
              isRequired
              id="mobile"
              {...register('mobile')}
              // onChange={handleInputChange}
              error={errors.mobile?.message as string}
            />
            {userData?.role === 'ORGANIZATION' && (
              <Input
                label="Description"
                placeholder="Enter optional description"
                {...register('description')}
                error={errors.description?.message as string}
              />
            )}

            {isLoadingMembers ? (
              <Loader />
            ) : watch('mobile') ? (
              <div
                className={`p-3 rounded my-2 ${receiverData?.isverify !== '1' || errors.mobile?.message || verifyError ? styles.error : styles.success} border-${receiverData?.isverify !== '1' || verifyError ? 'danger' : 'success'} d-flex justify-content-between align-items-center`}
              >
                <div>
                  <h6 className="mb-0 fw-bold">{watch('mobile')}</h6>
                  {verifyError && <span>{verifyError}</span>}
                </div>

                {receiverData ? (
                  receiverData?.isverify === '1' && !errors.mobile?.message ? (
                    <div className="d-flex gap-2">
                      <VerifyIcon />
                      <span className="fw-bold text-black">Verified</span>
                    </div>
                  ) : (
                    <div className="d-flex gap-2">
                      <VerifyIcon color="red" />
                      <span className="fw-bold text-black">Unverified</span>
                    </div>
                  )
                ) : null}
              </div>
            ) : null}
            <div className={styles.actions}>
              <Button
                variant="outline"
                onClick={() => {
                  onClose();
                  reset();
                }}
                disabled={isLoading}
                type="button"
              >
                Cancel
              </Button>
              <Button
                className="w-auto"
                variant="dark"
                type="submit"
                disabled={
                  (receiverData ? receiverData.isVerified == '0' : true) ||
                  isLoading
                }
              >
                Send Receive Token Request
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
};

export default ReceiveCashModal;
